import React, { Component } from 'react';
import Button from 'react-bootstrap/Button';
import { connect } from 'react-redux';
import i18n from '../../../i18n';
import axios from 'axios/axios-base';
import { Modal } from 'react-bootstrap';
import _ from 'lodash';
import 'components/transactionEditor/Wizards/ResponseNodeWidget.css';
import SimpleLoader from '../../loaders/SimpleLoader';
import { showToastNotification } from '../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../store/ui/toasts/toastsTypes';
import ResponseTreeWizard from './ResponseTreeWizard';
import { RequestTabState } from '../../../views/AssetCatalog/Transactions/TransactionEditor/RequestTab/RequestTab';
import HelpTooltip from '../../overlays/helpTooltip/HelpTooltip';
import BzmIcon from '../../icons/BzmIcon';
import { getXmlObject } from 'utils/xmlUtil';
import { BodyTemplateTypes } from '../ResponseBody';
import { Matcher } from '../../../types/rest/dslTypes';
import { resolveMatcherSampleBody } from 'utils/transactionUtils';

interface BodyTemplate {
  body: string;
  types: BodyTemplateTypes;
}

interface LocalProps {
  type: string;
  responseBody: string;
  responseBodyTemplate: BodyTemplate;
  requestTabState: RequestTabState;
  updateBodyContent: (content: string) => void;
  prettifyContent: (content: string) => void;
  serviceId?: number;
}

export interface LeafNodeResponseValue {
  id: string;
  value: string;
}

class ResponseNodeWidget extends Component<AllProps> {
  state = {
    showWidget: false,
    selectedNode: -1,
    sampleBodyJsonUi: '' as string,
    isTreeReady: false,
    leafNodeMatcherValues: [] as LeafNodeResponseValue[],
    requestBodyTreeJson: '' as string,
    requestBodyType: '' as string,
  };

  convertContent = (content: string) => {
    if (content !== '') {
      this.setState({
        isTreeReady: false,
      });
      const type = this.props.type === 'json' ? 'json' : 'xml';
      if (content) {
        axios
          .post(
            '/workspaces/{workspaceId}/ui/transactions/convert-to-tree?sourceType=' + type,
            Buffer.from(content).toString('base64'),
            {
              headers: {
                'Content-Type': 'text/plain',
                Accept: 'application/json',
              },
            },
          )
          .then(response => {
            this.setState({
              sampleBodyJsonUi: Buffer.from(response.data.result, 'base64').toString(),
              isTreeReady: true,
            });
            this.initializeLeafNodeValues();
          })
          .catch(error => {
            this.setState({
              isTreeReady: true,
              hasSampleBodyChanged: false,
            });
            const errorMsg = _.get(error, 'response.data.error', error.toString());
            this.props.showToastNotification(ToastType.Danger, '', errorMsg);
          });
      }
    }
  };

  convertRequestBodyToTree = (content: string, type: string) => {
    if (content !== '') {
      this.setState({
        isTreeReady: false,
      });
      if (content) {
        axios
          .post(
            '/workspaces/{workspaceId}/ui/transactions/convert-to-tree?sourceType=' + type,
            Buffer.from(content).toString('base64'),
            {
              headers: {
                'Content-Type': 'text/plain',
                Accept: 'application/json',
              },
            },
          )
          .then(response => {
            this.setState({
              requestBodyTreeJson: Buffer.from(response.data.result, 'base64').toString(),
              requestBodyType: type,
            });
          })
          .catch(error => {
            const errorMsg = _.get(error, 'response.data.error', error.toString());
            this.props.showToastNotification(ToastType.Danger, '', errorMsg);
          });
      }
    }
  };

  /**
   * Creates tree structure for the table component.
   */
  initializeLeafNodeValues = () => {
    let data: any = [];
    data = this.state.sampleBodyJsonUi ? JSON.parse(this.state.sampleBodyJsonUi) : data;
    const array = [] as LeafNodeResponseValue[];
    data.forEach((element: any) => {
      if (element.type === 'LEAF') {
        array.push({
          id: element.name + '_' + element.id,
          value: element.leafText,
        });
      }
    });
    this.setState({
      leafNodeMatcherValues: array,
    });
  };

  updateLeafNodeValue = (id: string, value: string) => {
    const index = this.state.leafNodeMatcherValues.findIndex(item => item.id === id);
    const newArray = this.state.leafNodeMatcherValues;
    if (index === -1) {
      newArray.push({
        id: id,
        value: value,
      });
    } else {
      newArray[index] = {
        id: id,
        value: value,
      };
    }
    this.setState({
      leafNodeValues: newArray,
    });
  };

  closeModal = () => {
    this.setState({ showWidget: false });
  };

  openWidget = () => {
    const content = this.props.responseBody ? this.props.responseBody : '';

    if (content !== '') {
      this.convertContent(content);
    } else {
      this.setState({
        isTreeReady: true,
      });
    }

    // Get Sample body from Request Body Matchers.
    // Loaded from the matcher's own sampleBody, falling back to the legacy,
    // read-only transaction-level sample body, then to the matcher's literal content.
    let sampleBody = '';
    const jsonIndex = _.findIndex(this.props.requestTabState.body, body => body.matcher.includes(Matcher.matches_json));
    if (jsonIndex !== -1) {
      sampleBody = this.props.requestTabState.body[jsonIndex].sampleBody || '';
    }
    if (sampleBody === '') {
      const xmlIndex = _.findIndex(this.props.requestTabState.body, body => body.matcher.includes(Matcher.matches_xml));
      if (xmlIndex !== -1) {
        sampleBody = this.props.requestTabState.body[xmlIndex].sampleBody || '';
      }
    }

    // Fall back to the legacy, read-only transaction-level sample body if no matcher has its own yet.
    if (
      sampleBody === '' &&
      _.find(this.props.requestTabState.body, body => body.matcher.includes(Matcher.matches_json))
    ) {
      sampleBody = resolveMatcherSampleBody(undefined, this.props.requestTabState.legacySampleBody, 'json');
    }
    if (
      sampleBody === '' &&
      _.find(this.props.requestTabState.body, body => body.matcher.includes(Matcher.matches_xml))
    ) {
      sampleBody = resolveMatcherSampleBody(undefined, this.props.requestTabState.legacySampleBody, 'xpath');
    }

    // If Sample Body is still not found, try to get it from the content attribute of the Body Matcher.
    // (the original way before samples were shared among multiple body matchers)
    if (sampleBody === '') {
      const index = _.findIndex(this.props.requestTabState.body, body => body.matcher.includes(Matcher.matches_json));
      if (index !== -1) {
        sampleBody = this.props.requestTabState.body[index].content;
      }
    }
    if (sampleBody === '') {
      const index = _.findIndex(this.props.requestTabState.body, body => body.matcher.includes(Matcher.matches_xml));
      if (index !== -1) {
        sampleBody = this.props.requestTabState.body[index].content;
      }
    }

    if (sampleBody && sampleBody.trim() !== '') {
      const domObj = getXmlObject(sampleBody);
      let requestBodyType = 'json';
      if (domObj.getElementsByTagName('parsererror').length === 0) {
        requestBodyType = 'xml';
      }
      this.convertRequestBodyToTree(sampleBody, requestBodyType);
    } else {
      this.setState({
        requestBodyTreeJson: '',
        requestBodyType: '',
      });
    }

    this.setState({ showWidget: true });
  };

  /**
   * Replaces template placeholders with real values.
   * @param template
   */
  replaceTemplateValues = (template: BodyTemplate) => {
    let body = template.body;
    for (let tmp = 0; tmp < this.state.leafNodeMatcherValues.length; tmp++) {
      //Check the type before replacing, if type is boolean, do not add quotes
      const search = '#' + this.state.leafNodeMatcherValues[tmp].id;
      const type = _.get(this.props.responseBodyTemplate.types, search);
      const replace = this.state.leafNodeMatcherValues[tmp].value;

      //For Bool value, replace also the wrapping quotes
      if (type === 'bool' && (replace === 'true' || replace === 'false')) {
        body = body.replace(`"${search}"`, replace);
      } else {
        body = body.replace(search, replace);
      }
    }
    return body;
  };

  selectNode = () => {
    const replacedTemplateValues = this.replaceTemplateValues(this.props.responseBodyTemplate);
    this.props.updateBodyContent(replacedTemplateValues);
    this.setState({ showWidget: false });
  };

  render() {
    const modalHeader = i18n.t('transactions:editor.editWizard');

    return (
      <React.Fragment>
        <Button
          id={'response-selection-wizard' + _.uniqueId()}
          onClick={() => this.props.prettifyContent(this.props.responseBody)}
          variant={'link'}
          className="btn btn-link btn-no-hover mb-1 mt-0 pb-2 pt-0 btn btn-link"
          title={i18n.t('transactions:editor.formatBody')}
        >
          <BzmIcon variant={'icon-bzm-timeline-view'} className={'icon-xs'} />
          <span className={'ml-3 font-size-base font-weight-semi-bold text-primary'}>
            {i18n.t('transactions:editor.formatBody')}
          </span>
        </Button>
        <div className="vdivider-response-wizard ml-2 mr-2"></div>
        <Button
          id={'response-selection-wizard' + _.uniqueId()}
          onClick={this.openWidget}
          variant={'link'}
          className="btn btn-link btn-no-hover mb-1 mt-0 pb-2 pt-0"
          title={i18n.t('transactions:editor.editWizard')}
        >
          <BzmIcon variant={'icon-bzm-setup-wizard'} className={'icon-xs'} />
          <span className={'ml-3 font-size-base font-weight-semi-bold text-primary'}>
            {i18n.t('transactions:editor.editWizard')}
          </span>
        </Button>
        <div className="vdivider-response-wizard ml-2 mr-2"></div>
        {this.state.showWidget && (
          <Modal
            id="widget-modal"
            show={true}
            centered={true}
            backdrop={'static'}
            size={'lg'}
            onHide={() => this.closeModal()}
          >
            <Modal.Header closeButton id="widget-modal-btn-close-icon">
              <Modal.Title>
                <div className={'d-flex'}>
                  <span className={'modal-title-dialog'}>{modalHeader}</span>
                  <HelpTooltip id={'request_response_widgets_help'} className={'ml-3 mt-1'} />
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className={'mb-5'}>
                {!this.state.isTreeReady && (
                  <SimpleLoader className={'mt-5'} loadingText={i18n.t('transactions:editor.loadingNodeText')} />
                )}
                {this.state.isTreeReady && (
                  <ResponseTreeWizard
                    sampleBodyJsonUi={this.state.sampleBodyJsonUi}
                    requestBodyTreeJson={this.state.requestBodyTreeJson}
                    requestBodyType={this.state.requestBodyType}
                    requestTabState={this.props.requestTabState}
                    selectedNode={this.state.selectedNode}
                    updateLeafNodeValue={this.updateLeafNodeValue}
                    serviceId={this.props.serviceId}
                  />
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={this.closeModal}
                className={'btn btn-link'}
                id={'response-modal-btn-cancel' + _.uniqueId()}
                variant={'link'}
                title={i18n.t('common:actions.cancel')}
              >
                {i18n.t('common:actions.cancel')}
              </Button>
              <Button
                onClick={this.selectNode}
                className={'pl-5 pr-5'}
                id={'response-modal-btn-select-node' + _.uniqueId()}
                title={i18n.t('common:actions.select')}
              >
                {i18n.t('common:actions.save')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </React.Fragment>
    );
  }
}
const mapStateToProps = () => ({});

const mapDispatchToProps = {
  showToastNotification,
};

type MapToStateProps = ReturnType<typeof mapStateToProps>;

type DispatchProps = typeof mapDispatchToProps;

type AllProps = MapToStateProps & DispatchProps & LocalProps;

export default connect(mapStateToProps, mapDispatchToProps)(ResponseNodeWidget);
